/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { objectSortFn, SortOrder } from 'utils/helpers/sorting';

import LargeSearch from 'public/assets/icons/large-search.svg';

import ListLoading from 'components/ListLoading/ListLoading';

import SortHeader from './SortHeader/SortHeader';

import TableProps, { TableColumn } from './types';
// eslint-disable-next-line import/order
import { Any } from 'types/others';

import * as S from './styles';

const Table: React.FC<TableProps> = ({
  data,
  columns,
  className,
  fixedHeader,
  loading = false,
  showHeader = true,
  sortDefault = true,
  showNotFoundContent = false,
  loadingLines,
  loadingStyle,
}) => {
  const [_data, setData] = useState(data);
  const [sort, setSort] =
    useState<{ selectedKey?: string; direction: SortOrder }>();

  const handleSort = (
    { key, dataKey, sortBy, sortType }: TableColumn,
    direction: SortOrder,
    defaultSort = false,
  ) => {
    const currentColumn = columns.find(c => c.key === key);

    setSort({ selectedKey: key, direction });

    if (!defaultSort || sortDefault) {
      if (currentColumn?.sortFunction) {
        const sortFn = currentColumn?.sortFunction;
        const customSortFunction =
          direction === 'ASC'
            ? sortFn
            : (a: Record<string, Any>, b: Record<string, Any>) =>
                sortFn(a, b) * -1;

        setData(_data?.sort(customSortFunction));
        return;
      }

      const sortedArray = objectSortFn(
        _data ?? [],
        sortBy ?? dataKey,
        direction,
        sortType,
      );

      setData(sortedArray);
    }
  };

  const handleDefaultSort = (defaultFilterColumn: TableColumn) => {
    const { defaultSort } = defaultFilterColumn;

    if (defaultSort) {
      handleSort(defaultFilterColumn, defaultSort, true);
    }
  };

  useEffect(() => {
    if (!loading) {
      setData(data);
      setSort({ direction: 'ASC' });
    }
  }, [data, loading]);

  useEffect(() => {
    if (!sort?.selectedKey) {
      const defaultFilterColumn = columns.filter(
        column => column?.defaultSort,
      )[0];

      if (defaultFilterColumn) {
        handleDefaultSort(defaultFilterColumn);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  return (
    <S.StyledTable
      data-testid="table"
      className={className}
      fixedHeader={fixedHeader}
    >
      {showHeader && (
        <thead data-testid="tableHead">
          <tr data-testid="tableHeadRow">
            {columns.map(c => (
              <th key={c.key} data-testid="tableHeadCell">
                {fixedHeader ? (
                  <S.SortHeaderContainer
                    data-testid={`sortHeaderContainer-${c.key}`}
                  >
                    <SortHeader
                      direction={sort?.direction}
                      selected={c.key === sort?.selectedKey}
                      sortable={c.sortable}
                      column={c}
                      onClick={handleSort}
                    >
                      {(c.titleRender && c.titleRender(c.title)) || c.title}
                    </SortHeader>
                  </S.SortHeaderContainer>
                ) : (
                  <SortHeader
                    direction={sort?.direction}
                    selected={c.key === sort?.selectedKey}
                    sortable={c.sortable}
                    column={c}
                    onClick={handleSort}
                  >
                    {(c.titleRender && c.titleRender(c.title)) || c.title}
                  </SortHeader>
                )}
              </th>
            ))}
          </tr>
        </thead>
      )}
      {loading ? (
        <tbody data-testid="tableBody">
          <tr data-testid="tableBodyRow">
            <S.TDNoBorder colSpan={columns.length} data-testid="tableLoading">
              <ListLoading
                loadingStyle={loadingStyle}
                linesLength={loadingLines}
              />
            </S.TDNoBorder>
          </tr>
        </tbody>
      ) : (
        <>
          {_data && _data?.length > 0 ? (
            <tbody data-testid="tableBody">
              {data?.map((row, i) => (
                <tr key={`row-${i}`} data-testid="tableBodyRow">
                  {columns.map((col, j) => (
                    <td key={`colValue-${j}`} data-testid="tableBodyCell">
                      {(col.render &&
                        col.render(row[col.dataKey] as never, row as never)) ||
                        row[col.dataKey]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ) : (
            <>
              {showNotFoundContent && (
                <tbody data-testid="tableBody">
                  <tr data-testid="tableBodyRow">
                    <S.TDNoBorder colSpan={columns.length}>
                      <S.NoResultsContainer data-testid="tableNoResults">
                        <LargeSearch />
                        <S.NoResultsText>
                          <FormattedMessage id="SEARCH.NO_RESULTS" />
                        </S.NoResultsText>
                      </S.NoResultsContainer>
                    </S.TDNoBorder>
                  </tr>
                </tbody>
              )}
            </>
          )}
        </>
      )}
    </S.StyledTable>
  );
};

export default Table;
